import { Dropdown, Section, NumberField, DatePicker } from 'm2m.components';
import { DatePickerBusinessDay } from 'components/DatePickerBussinessDay';
import MultaMoraFields from 'components/instrumentos/registro/criteriosRemuneracao/sections/MultaMoraSection/MultaMoraFields';
import InfoIcon from '@material-ui/icons/Info';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'store';
import { updateField, updateRemuneracaoField, updateCustoEmissaoField, updateRemuneracao } from 'store/modules/notaComercial/criteriosRemuneracao';
import { updateMetadata } from 'store/modules/notaComercial/caracteristicas';
import 'assets/style.css';
import { Moeda, TipoLiquidacao, FuncaoParticipante, TipoPagamento, TipoRegistroInstrumentoFinanceiro, BancoResponse, ParticipanteResponse } from 'common/genericTypes';
import { inicioRentabilidadeOptions, moedaOptionsNotaComercial } from 'common/genericOptions';
import { labelsCriteriosRemuneracao as labels } from 'pages/notaComercial/registro/labels';
import { useValidarCampos } from '../useValidarCampos';
import InvestidoresSection from 'pages/instrumentos/criteriosRemuneracao/investidoresSection';
import { EtapaCadastro } from 'common/registro/enums';
import { RemuneracaoSection } from 'components/instrumentos/registro/criteriosRemuneracao/remuneracaoSection';

const mapStateToProps = (state: RootState) => ({
  criteriosRemuneracao: state.notaComercialCadastro.criteriosRemuneracao,
  investidoresOptions: state.notaComercialCadastro.metadata.investidores,
  isDistribuicaoPublica: state.notaComercialCadastro.caracteristicas.distribuicaoPublica.value === 'SIM',
  isLiquidacaoDireta: state.notaComercialCadastro.caracteristicas.tipoLiquidacao.value === TipoLiquidacao.Direta,
  isParticipanteEmissor: state.auth.usuario.participante.funcao === FuncaoParticipante.Emissor,
  isParticipanteCredenciado: state.auth.usuario.participante.funcao === FuncaoParticipante.Credenciado,
  highlightErrors: state.notaComercialCadastro.highlightErrors,
  typeOfRegister: state.notaComercialCadastro.typeOfRegister,
  usuarioGrupoAcesso: state.auth.usuario.grupoAcesso,
  statusEmissao: state.notaComercialCadastro.caracteristicas.statusEmissao,
});

const mapDispatchToProps = {
  updateField,
  updateRemuneracaoField,
  updateCustoEmissaoField,
  updateMetadata,
};

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps & { statusCadastro?: string, bancos: BancoResponse[], participantes: ParticipanteResponse[] };

const CriteriosRemuneracao: React.FC<Props> = (props) => {
  const {
    criteriosRemuneracao: {
      quantidadeEmitida,
      valorNominalUnitarioEmissao,
      volumeEmissao,
      moedaEmissao,
      dataEmissao,
      dataLimiteIntegralizacao,
      dataVencimento,
      cotacaoInicial,
      inicioRentabilidade,
      dataInicioRentabilidade,
      remuneracao,
      custodiantes,
      custoEmissao: {
        custoTotal,
        feeLaqusBruto,
        feeLaqusLiquido,
        feeRepasse
      },
      tipoPagamento,
      periodicidadeAmortizacao,
      primeiraParcelaAmortizacao,
      percentualMulta,
      periodicidadeMulta,
      percentualMora,
      periodicidadeMora
    },
    isDistribuicaoPublica,
    isLiquidacaoDireta,
    isParticipanteEmissor,
    isParticipanteCredenciado,
    highlightErrors,
    typeOfRegister,
    updateField,
    updateRemuneracaoField,
    updateCustoEmissaoField,
    statusCadastro,
    statusEmissao,
    bancos,
    participantes,
  } = props;

  const showError = (label: string) => camposInvalidos.includes(label) && highlightErrors;

  const camposInvalidos = useValidarCampos(EtapaCadastro.CriteriosRemuneracao);

  const isAditamento = [
    TipoRegistroInstrumentoFinanceiro.Aditamento,
  ].includes(typeOfRegister);

  const [showCotacaoInicial, setShowCotacaoInicial] = useState<boolean>(false);

  useEffect(() => {
    updateField({ name: 'moedaEmissao', value: Moeda.BRL });
  }, [moedaEmissao, updateField]);

  useEffect(() => {
    if (valorNominalUnitarioEmissao && quantidadeEmitida) {
      const volume = valorNominalUnitarioEmissao * quantidadeEmitida;
      updateField({ name: 'volumeEmissao', value: Math.round((volume + Number.EPSILON) * 100) / 100 });
    }
  }, [valorNominalUnitarioEmissao, quantidadeEmitida, updateField]);

  useEffect(() => {
    if (moedaEmissao && moedaEmissao !== Moeda.BRL) {
      setShowCotacaoInicial(true);
    } else {
      setShowCotacaoInicial(false);
      updateField({ name: 'cotacaoInicial', value: undefined });
    }
  }, [moedaEmissao, setShowCotacaoInicial, updateField]);

  useEffect(() => {
    if (tipoPagamento === TipoPagamento.TabelaPrice) {
      updateField({ name: 'primeiraParcelaJuros', value: primeiraParcelaAmortizacao });
      updateField({ name: 'periodicidadeJuros', value: periodicidadeAmortizacao });
    }
  }, [tipoPagamento, primeiraParcelaAmortizacao, periodicidadeAmortizacao]);

  return (
    <div>
      <Section title={'Informações Financeiras'} defaultGrid>
        <NumberField
          label={labels.valorNominalUnitarioEmissao}
          value={valorNominalUnitarioEmissao}
          required
          onChange={(value) => updateField({ name: 'valorNominalUnitarioEmissao', value: value! })}
          numberType="eightDecimals"
          disabled={isAditamento}
          error={showError(labels.valorNominalUnitarioEmissao)}
        />
        <div>
          <NumberField
            label={labels.quantidadeEmitida}
            value={quantidadeEmitida}
            required
            onChange={(value) => updateField({ name: 'quantidadeEmitida', value: value! })}
            numberType="integer"
            error={showError(labels.quantidadeEmitida)}
            disabled={custodiantes.flatMap((c) => c.investidoresPrivados).length > 0}
          />
          {custodiantes.flatMap((c) => c.investidoresPrivados).length > 0 &&
            (
              <span>
                <InfoIcon style={{ marginTop: '10px', marginRight: '5px' }} />
                Para alterar as quantidades emitidas, deverá excluir os investidores selecionados
              </span>
            )}
        </div>
        <NumberField
          label={labels.volumeEmissao}
          disabled
          value={volumeEmissao}
          onChange={(value) => null}
          numberType="money"
        />
        <Dropdown
          label={labels.moedaEmissao}
          required
          options={moedaOptionsNotaComercial}
          value={moedaEmissao ?? ''}
          onChange={(value) => updateField({ name: 'moedaEmissao', value })}
          error={showError(labels.moedaEmissao)}
        />

        <DatePickerBusinessDay
          value={dataEmissao ?? null}
          label={labels.dataEmissao}
          required
          onChange={(value) => {
            updateField({ name: 'dataEmissao', value });
            if (inicioRentabilidade === 'DATA_DE_EMISSAO') {
              updateField({ name: 'dataInicioRentabilidade', value });
            }
          }}
          maxDate={dataVencimento}
          error={showError(labels.dataEmissao)}
          disabled={isAditamento}
          validateBusinessDay
          keyValue="dataEmissao"
        />

        <DatePickerBusinessDay
          value={dataVencimento ?? null}
          label={labels.dataVencimento}
          required
          onChange={(value) => updateField({ name: 'dataVencimento', value })}
          minDate={dataEmissao}
          error={showError(labels.dataVencimento)}
          validateBusinessDay
          keyValue="dataVencimento"
        />

        {showCotacaoInicial && (
          <NumberField
            label={labels.cotacaoInicial}
            value={cotacaoInicial}
            required={moedaEmissao ? moedaEmissao !== Moeda.BRL : false}
            onChange={(value) => updateField({ name: 'cotacaoInicial', value })}
            numberType="money"
            error={showError(labels.cotacaoInicial)}
          />
        )}
        
        <Dropdown
          label={labels.inicioRentabilidade}
          required
          options={inicioRentabilidadeOptions}
          value={inicioRentabilidade ?? ''}
          onChange={(value) => {
            updateField({ name: 'inicioRentabilidade', value });
            if (value === 'DATA_DE_EMISSAO') {
              updateField({ name: 'dataInicioRentabilidade', value: dataEmissao });
            } else if (value === 'DATA_DE_INTEGRALIZACAO') {
              updateField({ name: 'dataInicioRentabilidade', value: null });
            }
          }}
          error={showError(labels.inicioRentabilidade)}
        />

        {inicioRentabilidade !== 'DATA_DE_INTEGRALIZACAO' && (
          <DatePickerBusinessDay
            value={dataInicioRentabilidade ?? null}
            label={labels.dataInicioRentabilidade}
            onChange={(value) => updateField({ name: 'dataInicioRentabilidade', value })}
            minDate={dataEmissao! ?? new Date()}
            error={showError(labels.dataInicioRentabilidade)}
            disabled={inicioRentabilidade === 'DATA_DE_EMISSAO'}
            validateBusinessDay
            keyValue="dataInicioRentabilidade"
          />
        )}
        <DatePicker
          value={dataLimiteIntegralizacao ?? null}
          label={labels.dataLimiteIntegralizacao}
          onChange={(value) => {
            updateField({ name: 'dataLimiteIntegralizacao', value });
          }}
          minDate={dataLimiteIntegralizacao! ?? new Date()}
          error={showError(labels.dataLimiteIntegralizacao)}
        />
      </Section>
      
      <RemuneracaoSection
        remuneracao={remuneracao}
        onUpdateRemuneracaoField={updateRemuneracaoField}
        useValidarCampos={useValidarCampos}
        highlightErrors={highlightErrors}
        updateRemuneracao={updateRemuneracao}
      />
        <MultaMoraFields
            percentualMulta={percentualMulta}
            periodicidadeMulta={periodicidadeMulta}
            percentualMora={percentualMora}
            periodicidadeMora={periodicidadeMora}
            onUpdateField={updateField}
            showError={showError}
            useTextFields={true}
        />      
      
      {!isDistribuicaoPublica && !isParticipanteEmissor && !isParticipanteCredenciado && (
        <InvestidoresSection
          quantidadeTotal={quantidadeEmitida ?? 0}
          valorUnitario={valorNominalUnitarioEmissao ?? 0}
          custodiantesProps={custodiantes}
          instrumentoFinanceiroStatus={statusCadastro}
          criteriosRemuneracao={props.criteriosRemuneracao}
          updateField={updateField}
          statusEmissao={statusEmissao}
          bancos={bancos}
          custodiantes={participantes.filter(p => p.funcoes.some(f => f.funcao === FuncaoParticipante.Custodiante))}
        />
      )}

      <Section title={'Custo de Emissão'} defaultGrid>
        <NumberField
          label={labels.custoTotal}
          required
          value={custoTotal}
          onChange={(value) => updateCustoEmissaoField({ name: 'custoTotal', value: value! })}
          numberType="money"
          error={showError(labels.custoTotal)}
        />
        <NumberField
          label={labels.feeLaqusBruto}
          value={feeLaqusBruto}
          onChange={(value) => updateCustoEmissaoField({ name: 'feeLaqusBruto', value: value! })}
          numberType="money"
          disabled={isAditamento}
          error={showError(labels.feeLaqusBruto)}
        />
        <NumberField
          label={labels.feeLaqusLiquido}
          value={feeLaqusLiquido}
          onChange={(value) => updateCustoEmissaoField({ name: 'feeLaqusLiquido', value: value! })}
          numberType="money"
          disabled={isAditamento}
          error={showError(labels.feeLaqusLiquido)}
        />
        <NumberField
          label={labels.feeRepasse}
          value={feeRepasse}
          onChange={(value) => updateCustoEmissaoField({ name: 'feeRepasse', value: value! })}
          numberType="money"
          error={showError(labels.feeRepasse)}
        />
      </Section>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CriteriosRemuneracao);
